import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {Artifact, ArtifactComponentProps, ArtifactType} from 'llm/chat-library/ArtifactTypes';
import {
  LegacyDataImportSourceFilePreview,
  UploadedFileInfo,
} from 'llm/chat-library/layouts/LegacyDataImportSideBySideLayout';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';

/**
 * Application state structure for Legacy Data Import chats.
 * Only the fields we need for the source file artifact.
 */
interface LegacyDataImportAgentState {
  uploaded_files?: Array<UploadedFileInfo>;
}

export interface UseSourceFileArtifactOptions {
  /** The application state from the agent - will extract uploaded_files if present */
  latestApplicationState: object | null;
  /** The chat session ID, needed for building preview URLs */
  chatSessionId: string;
}

/** Data passed to the source file artifact component */
export interface SourceFileArtifactData {
  chatSessionId: string;
  fileInfo: UploadedFileInfo;
}

/** Type alias for source file artifact */
export type SourceFileArtifact = Artifact<SourceFileArtifactData>;

/**
 * Component that renders a source file artifact.
 * Receives the source file data as the `data` prop per the Artifact contract.
 */
export function SourceFileArtifactComponent({
  data,
  onClose,
}: ArtifactComponentProps<SourceFileArtifactData>): React.ReactElement {
  if (!data.fileInfo) {
    return (
      <div
        className={css(styles.emptyPanel)}
        {...getTestProps({component: 'SourceFileArtifact', element: 'empty'})}
      >
        <span className={css(styles.emptyText)}>No source file available</span>
      </div>
    );
  }

  return <LegacyDataImportSourceFilePreview chatSessionId={data.chatSessionId} fileInfo={data.fileInfo} />;
}

function isLegacyDataImportAgentState(state: object): state is LegacyDataImportAgentState {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'uploaded_files' in state;
}

/**
 * Hook for managing a source file artifact in the chat.
 *
 * This handles:
 * - Extracting the uploaded file info from application state
 * - Providing a component to render the source file preview
 *
 * @returns The artifact, or null if no source file exists
 */
export function useSourceFileArtifact({
  latestApplicationState,
  chatSessionId,
}: UseSourceFileArtifactOptions): SourceFileArtifact | null {
  // Extract uploaded file from the application state (first file only)
  const uploadedFile = useMemo(
    () =>
      latestApplicationState && isLegacyDataImportAgentState(latestApplicationState)
        ? (latestApplicationState?.uploaded_files?.[0] ?? null)
        : null,
    [latestApplicationState]
  );

  const artifact = useMemo((): SourceFileArtifact | null => {
    if (!uploadedFile) {
      return null;
    }
    return {
      artifactType: ArtifactType.SOURCE_FILE,
      // Source files don't need to be "opened" in an external editor
      open: () => {},
      data: {
        chatSessionId,
        fileInfo: uploadedFile,
      },
      Component: SourceFileArtifactComponent,
    };
  }, [uploadedFile, chatSessionId]);

  return artifact;
}

const styles = StyleSheet.create({
  emptyPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: GRAY_LIGHTER,
    borderRadius: 4,
    border: `1px dashed ${GRAY_LIGHT}`,
    margin: 16,
  },
  emptyText: {
    color: GRAY_LIGHT,
    fontSize: 14,
  },
});
